// SummaryWithModal.jsx
import React, { useEffect, useRef } from 'react';

export function SummaryWithModal({ text, summaries, fallbackText, modalId, btnLabel = 'Details', title = 'Summary' }) {
  const root = useRef(null);

  // If Bootstrap JS is loaded, enable real tooltips; else CSS fallback still works
  useEffect(() => {
    if (!root.current || !window.bootstrap) return;
    const els = root.current.querySelectorAll('[data-bs-toggle="tooltip"]');
    const instances = [...els].map(el => new window.bootstrap.Tooltip(el));
    return () => instances.forEach(i => i.dispose());
  }, []);

  // Determine what to show - prioritize new summaries structure, fallback to old text prop
  const hasExecutive = summaries?.executive?.content;
  const hasWriter = summaries?.writer?.content;
  const displayText = hasExecutive || hasWriter || text || fallbackText;
  
  if (!displayText) return <span className="text-muted">—</span>;

  // For tooltip, show the first available summary (executive takes priority)
  const tooltipText = hasExecutive || text || fallbackText;

  return (
    <div ref={root} className="d-flex align-items-center gap-2">
      {/* Truncated inline with rich tooltip */}
      <span
        className="d-inline-block text-truncate small summary-tooltip"
        style={{ maxWidth: '100%' }}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        data-bs-html="true"
        title={`<div class='text-start summary-tip'><div class='fw-semibold mb-1'>${title}</div><div>${escapeHtml(
          tooltipText
        )}</div></div>`}
      >
        {tooltipText}
      </span>

      {/* Modal trigger */}
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none"
        data-bs-toggle="modal"
        data-bs-target={`#${modalId}`}
        aria-label="Open full summary"
      >
        ⓘ
      </button>

      {/* Modal */}
      <div className="modal fade" id={modalId} tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              {/* Show both summaries with labels if available, otherwise show fallback */}
              {hasExecutive || hasWriter ? (
                <div className="summary-modal-content">
                  {hasExecutive && (
                    <div className="mb-4">
                      <h6 className="fw-bold border-bottom pb-1 mb-2 text-primary">
                        {summaries.executive.label || 'For Executives'}
                      </h6>
                      <div className="summary-modal-text" style={{ whiteSpace: 'pre-wrap' }}>
                        {summaries.executive.content}
                      </div>
                    </div>
                  )}
                  {hasWriter && (
                    <div>
                      <h6 className="fw-bold border-bottom pb-1 mb-2 text-success">
                        {summaries.writer.label || 'For Writers'}
                      </h6>
                      <div className="summary-modal-text" style={{ whiteSpace: 'pre-wrap' }}>
                        {summaries.writer.content}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="summary-modal-text">{text || fallbackText}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// tiny helper to keep tooltip HTML safe
function escapeHtml(s = '') {
  return s.replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}
